import { useState } from 'react';
import PropTypes from 'prop-types';
import { RadioButton } from 'components/UI/RadioButton/RadioButton';
import { Callout } from 'components/UI/Callout/Callout';
import { Checkbox } from 'components/UI/Checkbox/Checkbox';
import { DropdownWithMenu } from 'components/UI/DropdownWithMenu/DropdownWithMenu';
import { InputControlled } from 'components/UI/InputControlled/InputControlled';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { accountMembershipSchema, emptySchema } from 'utils/form-validation-schema';
import { addMembershipToCart } from 'services/UserAccountService';
import { shapeAddMembershipPayload } from '../membership-utils';
import { NomMemberPersonalInformation } from './NomMemberPersonalInformation';
import './NonMemberContent.scss';

export const NonMemberContent = (props) => {
  const {
    FamiliesInfo,
    BequestsInfo,
    Account: { Title, DOBDay, DOBMonth, DOBYear },
    Prefixes,
    DateOfBirth,
  } = props;
  const [secondaryContactIsOpen, setSecondaryContactIsOpen] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [familiesInfo, setFamiliesInfo] = useState(false);
  const [bequestsInfo, setBequestsInfo] = useState(false);
  const [selectedPrefix, setSelectedPrefix] = useState(Title);
  const [selectedDobDay, setSelectedDobDay] = useState(DOBDay);
  const [selectedDobMonth, setSelectedDobMonth] = useState(DOBMonth);
  const [selectedDobYear, setSelectedDobYear] = useState(DOBYear);
  const [selectedMemberShip, setSelectedMemberShip] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(secondaryContactIsOpen ? accountMembershipSchema : emptySchema),
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    setValue,
  } = methods;

  const membershipFormSubmitHandler = async (inputsData) => {
    const requestPayload = shapeAddMembershipPayload({
      payloadFromSC: props,
      inputsData,
      dropdownsData: {
        selectedDobDay,
        selectedDobMonth,
        selectedDobYear,
        selectedPrefix,
      },
      familiesInfo,
      bequestsInfo,
      selectedMemberShip,
    });

    const response = await addMembershipToCart(requestPayload);
    if (response?.Redirect) {
      window.location.href = response.RedirectUrl;
    }

    if (response.ErrorMessage) {
      setErrorMessage(response.ErrorMessage);
    }
  };

  const radioButtonClickHandler = (event, membershiptypeId) => {
    setSelectedMemberShip(event.target.defaultValue);

    if (membershiptypeId === 'doubleOneYear' || membershiptypeId === 'doubleTwoYear') {
      setSecondaryContactIsOpen(true);
    } else {
      setSecondaryContactIsOpen(false);
    }
    setSubmitButtonDisabled(false);
  };

  const familiesInfoClickHandler = (event) => {
    setFamiliesInfo(event.target.checked);
  };

  const bequestsInfoClickHandler = (event) => {
    setBequestsInfo(event.target.checked);
  };

  const prefixDropDownOptions = Prefixes.map((prefix) => ({
    key: prefix.Description,
    name: prefix.Description,
  }));

  const prefixDropdownUpdateHandler = (prefix) => {
    setSelectedPrefix(prefix);
  };

  const getDobDropDownOptions = (DobOptions) =>
    DobOptions.map((item) => ({
      key: item,
      name: item.toString(),
    }));

  const dobDayDropdownUpdateHandler = (day) => {
    setSelectedDobDay(day);
  };

  const dobMonthDropdownUpdateHandler = (month) => {
    setSelectedDobMonth(month);
  };

  const dobYearDropdownUpdateHandler = (year) => {
    setSelectedDobYear(year);
  };

  const submitButtonClickHandler = () => {
    if (!isValid) {
      setErrorMessage('Please fill in the secondary member information');

      Object.keys(errors).forEach((key) => {
        setValue(key, '');
      });
    } else {
      setErrorMessage('');
    }
  };

  return (
    <section>
      <h2>Membership</h2>
      <p className="large">
        <strong>You currently do not have an Arts Centre Melbourne membership.</strong>
        <br />
        For more information on all the exclusive benefits of being a member, please visit our{' '}
        <a href="/join-support/membership/arts-centre-melbourne-membership">Membership page</a>.
      </p>
      <p className="large">
        If you'd like to become a member with us, please select one of the membership options below.
      </p>
      <h3>Membership options</h3>

      <FormProvider {...methods}>
        <form className="form-membership" onSubmit={handleSubmit(membershipFormSubmitHandler)}>
          <div className="membership-type">
            <RadioButton
              id="singleOneYear"
              label="One-year single membership - $195"
              name="membershipType"
              onChangeHandler={(event) => radioButtonClickHandler(event, 'singleOneYear')}
              value="One year single"
              extendClass="label-unbold"
            />
            <RadioButton
              id="doubleOneYear"
              label="One-year double membership - $295"
              name="membershipType"
              onChangeHandler={(event) => radioButtonClickHandler(event, 'doubleOneYear')}
              value="One year double"
              extendClass="label-unbold"
            />
            <RadioButton
              id="singleTwoYear"
              label="Two-year single membership - $340"
              name="membershipType"
              onChangeHandler={(event) => radioButtonClickHandler(event, 'singleTwoYear')}
              value="Two year single"
              extendClass="label-unbold"
            />
            <RadioButton
              id="doubleTwoYear"
              label="Two-year double membership - $540"
              name="membershipType"
              onChangeHandler={(event) => radioButtonClickHandler(event, 'doubleTwoYear')}
              value="Two year double"
              extendClass="label-unbold"
            />
            <br />
            <p>
              <em>
                <strong>Please note:</strong> To be eligible to join the Double membership, both
                members must reside at the same address.
              </em>
            </p>
          </div>

          <div className="membership-personal-info">
            <NomMemberPersonalInformation {...props} />

            {secondaryContactIsOpen && (
              <div>
                <h4>Secondary member information</h4>
                <p>All fields required unless otherwise marked.</p>
                <div className="membership-info-section">
                  <div className="row">
                    <div className="col-sm-6 col-md-5 col-lg-4">
                      <div className="form-group">
                        <DropdownWithMenu
                          singleColumn
                          hideDefaultItem
                          dropdownChangeHandler={prefixDropdownUpdateHandler}
                          defaultChosenValue={`${selectedPrefix || 'Title (optional)'}`}
                          defaultValue={`${selectedPrefix || 'Title (optional)'} `}
                          menuData={prefixDropDownOptions}
                        />
                      </div>

                      <div className="form-group">
                        <InputControlled
                          type="text"
                          id="firstName"
                          labelText="First name"
                          isRequired
                          hideErrors
                        />
                      </div>

                      <div className="form-group">
                        <InputControlled
                          type="text"
                          id="lastName"
                          labelText="Last name"
                          isRequired
                          hideErrors
                        />
                      </div>

                      <div className="form-group">
                        <InputControlled
                          type="text"
                          id="phoneNumber"
                          labelText="Phone Number"
                          isRequired
                          hideErrors
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="dobday" className="control-label static-label">
                          Date of birth (optional)
                        </label>
                        <div className="row">
                          <div className="col-xs-4">
                            <DropdownWithMenu
                              singleColumn
                              hideDefaultItem
                              dropdownChangeHandler={dobDayDropdownUpdateHandler}
                              defaultChosenValue={`${selectedDobDay || 'DD'}`}
                              menuData={getDobDropDownOptions(DateOfBirth.Days)}
                            />
                          </div>
                          <div className="col-xs-4">
                            <DropdownWithMenu
                              singleColumn
                              hideDefaultItem
                              dropdownChangeHandler={dobMonthDropdownUpdateHandler}
                              defaultChosenValue={`${selectedDobMonth || 'MM'}`}
                              menuData={getDobDropDownOptions(DateOfBirth.Months)}
                            />
                          </div>

                          <div className="col-xs-4">
                            <DropdownWithMenu
                              singleColumn
                              hideDefaultItem
                              dropdownChangeHandler={dobYearDropdownUpdateHandler}
                              defaultChosenValue={`${selectedDobYear || 'YYYY'}`}
                              menuData={getDobDropDownOptions(DateOfBirth.Years)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="callout-container callout-info">
            <label className="static-label">Get even more engaged in our programs</label>
            <Checkbox
              checkboxChangeHandler={familiesInfoClickHandler}
              label="I would like more information on how I can support Arts Centre Melbourne's program for
              children, families and young people."
              isChecked={FamiliesInfo}
              id="FamiliesInfo"
            />

            <Checkbox
              checkboxChangeHandler={bequestsInfoClickHandler}
              label="I would like more information on leaving a bequest to Arts Centre Melbourne"
              isChecked={BequestsInfo}
              id="BequestsInfo"
            />
          </div>

          <div className="row">
            <div className="col-md-8">
              {errorMessage && <Callout errorMessage={errorMessage} />}
            </div>

            <div className="col-xs-12">
              <button
                type="submit"
                className="btn btn-secondary btn-cta pull-right"
                disabled={submitButtonDisabled}
                onClick={submitButtonClickHandler}
              >
                Add to cart
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </section>
  );
};

NonMemberContent.propTypes = {
  errorMessage: PropTypes.string,
  FamiliesInfo: PropTypes.bool,
  BequestsInfo: PropTypes.bool,
  Account: PropTypes.object,
  Prefixes: PropTypes.array,
  DateOfBirth: PropTypes.object,
};
